import axios from "axios";
import {apiUrl} from "../../services.config";
import {reportApiFailure} from "../monitoring";

// iOS (NSURLErrorNetworkConnectionLost) and Android WebViews intermittently fail
// a request when it reuses a keep-alive connection the OS has silently dropped —
// surfacing as a response-less ERR_NETWORK. A timed-out request surfaces as
// ECONNABORTED. Apple's guidance is to simply retry. We retry idempotent GETs
// only, with short backoff, then report to Sentry exactly once if all attempts
// fail. Non-retryable errors (4xx/5xx, cancellations, non-GET) report as before.
const REQUEST_TIMEOUT_MS = 15000;
const RETRY_BACKOFFS_MS = [300, 800]; // length === max retries (2); 3 attempts total

const apiClient = axios.create({
    baseURL: apiUrl,
    timeout: REQUEST_TIMEOUT_MS,
    withCredentials: true,
    headers: {
        "X-Requested-With": "XMLHttpRequest",
        // Bypass ngrok's free-tier browser interstitial (ERR_NGROK_6024), which
        // otherwise intercepts GET requests and returns an HTML warning page with
        // no CORS headers. Ignored by the production backend.
        "ngrok-skip-browser-warning": "true",
    },
});

const isRetryable = (error: any): boolean => {
    const method = (error?.config?.method ?? "").toLowerCase();
    const noResponse = !error?.response;
    const code = error?.code;
    return (
        method === "get" &&
        noResponse &&
        (code === "ERR_NETWORK" || code === "ECONNABORTED")
    );
};

const delay = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

// Registered at module load (not in a React effect) so it cannot be outraced by
// requests that fire during app startup. Retries transient GET failures, then
// reports infra/unexpected failures to Sentry and re-rejects so existing
// per-service error handling is unchanged.
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error?.config;
        if (config && isRetryable(error)) {
            const attempt: number = config.__retryCount ?? 0;
            if (attempt < RETRY_BACKOFFS_MS.length) {
                config.__retryCount = attempt + 1;
                await delay(RETRY_BACKOFFS_MS[attempt]);
                return apiClient(config);
            }
        }
        reportApiFailure(error);
        return Promise.reject(error);
    }
);

export default apiClient;
